import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FontAwesome from '@expo/vector-icons/FontAwesome';

interface CallControlsProps {
    isMuted: boolean;
    isSpeakerOn: boolean;
    onToggleMute: () => void;
    onToggleSpeaker: () => void;
    onHangup: () => void;
    /** Disables mute/speaker before the room is joined (hang up always works). */
    disabled?: boolean;
    /** Speaker/earpiece routing only applies on native; hidden on web. */
    showSpeaker?: boolean;
}

/** Bottom control bar for an active call: mute, speaker, hang up. Audio only. */
export function CallControls({
    isMuted,
    isSpeakerOn,
    onToggleMute,
    onToggleSpeaker,
    onHangup,
    disabled,
    showSpeaker = true,
}: CallControlsProps) {
    return (
        <View style={styles.row}>
            <ControlButton
                icon={isMuted ? 'microphone-slash' : 'microphone'}
                label={isMuted ? 'Unmute' : 'Mute'}
                active={isMuted}
                disabled={disabled}
                onPress={onToggleMute}
            />

            <TouchableOpacity
                style={[styles.button, styles.hangupButton]}
                activeOpacity={0.8}
                onPress={onHangup}
                accessibilityRole="button"
                accessibilityLabel="Hang up"
            >
                <FontAwesome name="phone" size={28} color="#FFF" />
            </TouchableOpacity>

            {showSpeaker && (
                <ControlButton
                    icon={isSpeakerOn ? 'volume-up' : 'volume-down'}
                    label={isSpeakerOn ? 'Speaker' : 'Earpiece'}
                    active={isSpeakerOn}
                    disabled={disabled}
                    onPress={onToggleSpeaker}
                />
            )}
        </View>
    );
}

interface ControlButtonProps {
    icon: React.ComponentProps<typeof FontAwesome>['name'];
    label: string;
    active?: boolean;
    disabled?: boolean;
    onPress: () => void;
}

function ControlButton({ icon, label, active, disabled, onPress }: ControlButtonProps) {
    return (
        <View style={styles.controlItem}>
            <TouchableOpacity
                style={[styles.button, active && styles.buttonActive, disabled && styles.buttonDisabled]}
                activeOpacity={0.8}
                onPress={onPress}
                disabled={disabled}
                accessibilityRole="button"
                accessibilityLabel={label}
            >
                <FontAwesome name={icon} size={24} color={active ? '#1A1A1A' : '#FFF'} />
            </TouchableOpacity>
            <Text style={styles.label}>{label}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-around',
        paddingHorizontal: 24,
    },
    controlItem: {
        alignItems: 'center',
        gap: 8,
    },
    button: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: 'rgba(255, 255, 255, 0.18)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonActive: {
        backgroundColor: '#FFF',
    },
    buttonDisabled: {
        opacity: 0.4,
    },
    hangupButton: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: '#E53935',
        transform: [{ rotate: '135deg' }],
    },
    label: {
        fontSize: 12,
        color: 'rgba(255, 255, 255, 0.85)',
    },
});
